import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import {
  Activity, ArrowRight, Bus, CameraOff, ChevronRight, DoorOpen, Footprints, Gauge, Gift, Landmark,
  MapPin, Play, Radio, ShieldAlert, Timer, Train, TrendingUp, Users, Utensils, Video, Warehouse,
} from 'lucide-react';
import { baseapi } from '../baseapi';
import { useFestivalDashboard } from '../hooks/useFestivalDashboard';
import { CrowdProvider, useCrowd } from '../Demoportal/context/CrowdContext';
import { CONFIG, HOLDING_COMPLEXES, NODES, ORIGINS, QLINES, QSTAGES, STAGE_ACTIVE_LINES, XSTAGES } from '../Demoportal/config/crowdConfig';
import { cellArea, hhmm, qKey, xArea } from '../Demoportal/utils/crowdMath';
import { zoneLabelFromId } from '../utils/zoneLabel';

/**
 * Overview — one screen for the whole pilgrim flow: City → Holding → Queue → Temple → Exit.
 *
 * Two data modes, never mixed on one screen:
 *  - "Live cameras": only figures counted by cameras (dashboard + summary APIs). A block whose cameras are not
 *    installed yet keeps its structure and shows "—" with a "No camera" tag. Nothing is estimated.
 *  - "Simulation model": the same crowd model that drives the Holding Area / Queue Operations / Digital Twin
 *    tabs, so the complete layout can be reviewed before the temple-side cameras go live.
 */

type Mode = 'live' | 'model';
type Num = number | null;
type Level = 'NORMAL' | 'ELEVATED' | 'HIGH' | null;

interface CityNode { id: string; name: string; total: Num; rate: Num; x: number; y: number; kind: 'rail' | 'bus' | 'walk' | 'junction' | 'temple'; place?: 'above' | 'below' | 'right' | 'left' }
interface HoldBox { id: string; count: Num; cap: number; gate: string }
interface HoldArea { id: string; name: string; location: string; count: Num; cap: number; inflow: Num; wait: Num; nextRelease: string | null; fillNext: string | null; boxes: HoldBox[] }
/** count = people standing in the zone now; passed = people counted past its entry line since 00:00 (not an occupancy). */
interface QZone { id: string; name: string; count: Num; cap: Num; density: Num; dwell: Num; inRate: Num; outRate: Num; eta: Num; passed?: Num }
interface Decision { icon: React.ReactNode; label: string; value: string; note: string; right?: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'muted' }
interface VM {
  clock: string;
  kpis: { total: Num; approaches: Num; holding: Num; inQueue: Num; inside: Num; insideCap: Num; darshan: Num; exited: Num };
  level: Level; levelNote: string;
  city: { nodes: CityNode[]; entries: { name: string; value: Num; pct: Num }[]; total: Num };
  holding: HoldArea[];
  zones: QZone[];
  temple: { inside: Num; cap: Num; inRate: Num; outRate: Num };
  services: { id: string; name: string; people: Num; rate: Num }[];
  chart: { points: { t: string; a: Num; b: Num }[]; title: string; aName: string; bName: string; forecastFrom?: string };
  decisions: Decision[];
  cameras: { online: Num; total: Num };
  queueWait: Num;
}

const fmt = (n: Num | undefined) => (n == null ? '—' : Math.round(n).toLocaleString('en-IN'));
const pct = (a: Num, b: Num) => (a == null || !b ? null : Math.round((a / b) * 100));
const CARD = 'rounded-xl bg-white dark:bg-zinc-900 border border-neutral-200 dark:border-[#262c39] shadow-xs';
const SUB = 'text-neutral-500 dark:text-neutral-400';

/* occupancy bands used across the page (same thresholds as the legend) */
const band = (p: number | null) =>
  p == null ? { bar: 'bg-neutral-300 dark:bg-neutral-700', text: 'text-neutral-400', name: 'No data' }
    : p > 90 ? { bar: 'bg-rose-500', text: 'text-rose-500', name: 'Full' }
      : p > 75 ? { bar: 'bg-amber-500', text: 'text-amber-500', name: 'High' }
        : p >= 50 ? { bar: 'bg-violet-500', text: 'text-violet-500', name: 'Filling' }
          : { bar: 'bg-emerald-500', text: 'text-emerald-500', name: 'Available' };

const NoCam: React.FC<{ text?: string }> = ({ text = 'No camera' }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-neutral-500/10 text-neutral-400 border border-neutral-500/20 whitespace-nowrap">
    <CameraOff className="w-2.5 h-2.5" />{text}
  </span>
);

/* ------------------------------------------------------------------ live view-model (cameras only) */
function useLiveVM(enabled: boolean): VM {
  const { data } = useFestivalDashboard();
  const [headline, setHeadline] = useState<Record<string, { m1: number; m5: number; m15: number; total: number }> | null>(null);
  const [now, setNow] = useState(new Date());
  // GET /api/overview: the whole screen computed on the server from camera data. null while it has not
  // answered or on a server without it (then the figures are put together here from dashboard + summary).
  const [api, setApi] = useState<any | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const loadOverview = () => baseapi.get('overview')
      .then(r => { if (alive) setApi(r.data && Array.isArray(r.data.zones) ? r.data : null); })
      .catch(() => { if (alive) setApi(null); });
    loadOverview();
    const t = window.setInterval(loadOverview, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => baseapi.get('summary').then(r => { if (alive) setHeadline(r.data?.headline || null); }).catch(() => undefined);
    load();
    const t = window.setInterval(load, 5000);
    const c = window.setInterval(() => setNow(new Date()), 1000);
    return () => { alive = false; window.clearInterval(t); window.clearInterval(c); };
  }, [enabled]);

  return useMemo<VM>(() => {
    const clock = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
    if (api) {
      // numbers from the server; layout (map positions, icons) stays here
      const POS: Record<string, Pick<CityNode, 'x' | 'y' | 'place'>> = {
        RAIL: { x: 300, y: 70, place: 'above' }, BUS: { x: 318, y: 190, place: 'below' },
        KUMMARI: { x: 58, y: 108, place: 'above' }, GEDDA: { x: 248, y: 128, place: 'right' },
        VINAYAKA: { x: 186, y: 158, place: 'above' }, TEMPLE: { x: 128, y: 182, place: 'below' },
      };
      const ICON: Record<string, React.ReactNode> = {
        arrivals: <TrendingUp className="w-3.5 h-3.5" />, busiest: <Activity className="w-3.5 h-3.5" />,
        peak: <Timer className="w-3.5 h-3.5" />, cameras: <Video className="w-3.5 h-3.5" />,
        pressure: <Gauge className="w-3.5 h-3.5" />, alert: <ShieldAlert className="w-3.5 h-3.5" />,
      };
      return {
        clock,
        kpis: api.kpis,
        level: api.level ?? null, levelNote: api.levelNote ?? '',
        city: {
          nodes: (api.city?.nodes ?? []).filter((n: any) => POS[n.id]).map((n: any) => ({ ...n, ...POS[n.id] })),
          entries: api.city?.entries ?? [], total: api.city?.total ?? null,
        },
        holding: (api.holding ?? []).map((h: any) => ({
          id: h.id, name: h.name, location: h.location ?? '', count: h.count ?? null, cap: h.cap, inflow: h.inflow ?? null,
          wait: h.wait ?? null, nextRelease: h.nextRelease ?? null, fillNext: h.fillNext ?? null,
          boxes: (h.boxes ?? []).map((b: any) => ({ id: b.id, count: b.count ?? null, cap: b.cap, gate: b.gate ?? 'unknown' })),
        })),
        zones: api.zones,
        temple: api.temple,
        services: api.services ?? [],
        chart: api.chart,
        decisions: (api.decisions ?? []).map((d: any) => ({ ...d, icon: ICON[d.key] ?? <Activity className="w-3.5 h-3.5" /> })),
        cameras: api.cameras ?? { online: null, total: null },
        queueWait: api.queueWait ?? null,
      };
    }
    const k = data?.kpis;
    const cp = (id: string) => data?.checkpoints?.find(c => c.id === id)?.people.toward ?? null;
    const h = (key: string) => headline?.[key] ?? null;
    const stages = data?.queue_stages ?? [];
    const measured = stages.filter(s => s.source === 'camera' && s.count != null);
    const feeders = data?.feeder_share ?? [];

    const nodes: CityNode[] = [
      { id: 'RAIL', name: 'Railway Station', total: h('rail_out')?.total ?? null, rate: h('rail_out')?.m1 ?? null, x: 300, y: 70, kind: 'rail', place: 'above' },
      { id: 'BUS', name: 'PNBS Bus Station', total: h('bus_out')?.total ?? null, rate: h('bus_out')?.m1 ?? null, x: 318, y: 190, kind: 'bus', place: 'below' },
      { id: 'KUMMARI', name: 'Kummaripalem Jn', total: h('kummari_toward')?.total || cp('KUMMARI-JN'), rate: h('kummari_toward')?.total ? h('kummari_toward')!.m1 : null, x: 58, y: 108, kind: 'walk', place: 'above' },
      { id: 'GEDDA', name: 'Geddabomma Centre', total: cp('GEDDABOMMA'), rate: null, x: 248, y: 128, kind: 'junction', place: 'right' },
      { id: 'VINAYAKA', name: 'Vinayaka Temple Jn', total: cp('VINAYAKA'), rate: null, x: 186, y: 158, kind: 'junction', place: 'above' },
      { id: 'TEMPLE', name: 'Kanaka Durga Temple', total: h('final_in')?.total || null, rate: h('final_in')?.total ? h('final_in')!.m1 : null, x: 128, y: 182, kind: 'temple', place: 'below' },
    ];

    const approaches = [
      { name: 'Railway Station', v: h('rail_out')?.m5 ?? 0 },
      { name: 'PNBS Bus Station', v: h('bus_out')?.m5 ?? 0 },
      { name: 'Kummaripalem', v: h('kummari_toward')?.m5 ?? 0 },
    ].sort((a, b) => b.v - a.v);
    const change = k?.total_footfall.change_pct ?? null;
    const offline = k?.cctv.offline ?? null;

    const hourNow = now.getHours();
    const points = (data?.hourly ?? []).filter((_, i) => i < hourNow)   // completed hours only: the running hour would read as a false dip
      .map(x => ({ t: x.hour, a: x.inflow, b: x.outflow }));

    const decisions: Decision[] = [
      { icon: <TrendingUp className="w-3.5 h-3.5" />, label: 'Arrivals · last 15 min', value: `${fmt(k?.total_footfall.last_15m)} people`, note: change == null ? 'No previous-day figure' : `${change >= 0 ? '+' : ''}${change}% vs yesterday (day total)`, tone: 'info' },
      { icon: <Activity className="w-3.5 h-3.5" />, label: 'Busiest approach', value: headline ? approaches[0].name : '—', note: headline ? `${fmt(approaches[0].v / 5)} people/min over 5 min` : 'Waiting for the analytics API', tone: 'warn' },
      { icon: <Timer className="w-3.5 h-3.5" />, label: 'Peak hour today', value: k?.peak_surge.from ? `${k.peak_surge.from} – ${k.peak_surge.to}` : '—', note: k?.peak_surge.rate_per_hr != null ? `${fmt(k.peak_surge.rate_per_hr)} arrivals in the hour` : 'Not enough data yet', tone: 'info' },
      { icon: <Video className="w-3.5 h-3.5" />, label: 'Active cameras', value: `${fmt(k?.cctv.online)} / ${fmt(k?.cctv.total)}`, note: offline ? `${offline} offline — check Junctions tab` : 'All online', right: offline ? 'Check' : 'OK', tone: offline ? 'warn' : 'ok' },
      { icon: <Gauge className="w-3.5 h-3.5" />, label: 'Queue & temple pressure', value: 'Not measured', note: measured.length ? `${measured.length} of ${stages.length} stages count passes only; no occupancy cameras yet` : 'Q-line and temple cameras are not installed yet', tone: 'muted' },
      { icon: <ShieldAlert className="w-3.5 h-3.5" />, label: 'Alert level', value: '—', note: 'Needs queue density cameras', tone: 'muted' },
    ];

    return {
      clock,
      kpis: {
        total: k?.total_footfall.value ?? null, approaches: k?.total_footfall.last_15m ?? null,
        holding: k?.holdings_load.source === 'camera' ? k.holdings_load.value : null,
        inQueue: null,
        inside: k?.in_hill.value ?? null, insideCap: null, darshan: null, exited: k?.total_exited.value ?? null,
      },
      level: null, levelNote: 'Queue cameras pending',
      city: { nodes, entries: feeders.map(f => ({ name: f.name, value: f.value, pct: f.pct })), total: k?.total_footfall.value ?? null },
      holding: HOLDING_COMPLEXES.map(ha => ({
        id: ha.id, name: ha.name.split('·')[0].trim(), location: ha.location, count: null, cap: ha.totalCapacity, inflow: null, wait: null,
        nextRelease: null, fillNext: null,
        boxes: ha.rooms.map(r => ({ id: r.id, count: null, cap: r.capacity, gate: 'unknown' })),
      })),
      // the dashboard API's stage figure is a pass count at the stage's entry line since 00:00, not how many stand in the zone
      zones: stages.map(s => ({ id: s.id, name: s.name, count: null, cap: s.capacity, density: null, dwell: null, inRate: s.source === 'camera' && s.last_15m != null ? s.last_15m / 15 : null, outRate: null, eta: null, passed: s.source === 'camera' ? s.count : null })),
      temple: { inside: k?.in_hill.value ?? null, cap: null, inRate: null, outRate: null },
      services: [
        { id: 'prasadam', name: 'Prasadam area', people: null, rate: null },
        { id: 'annadanam', name: 'Annaprasadam', people: null, rate: null },
        { id: 'exit', name: 'Exit corridor', people: null, rate: null },
      ],
      chart: { points, title: 'City throughput — today, per completed hour', aName: 'Arrivals toward temple', bName: 'Returning' },
      decisions,
      cameras: { online: k?.cctv.online ?? null, total: k?.cctv.total ?? null },
      queueWait: null,
    };
  }, [data, headline, now, api]);
}

/* ------------------------------------------------------------------ simulation view-model */
function useModelVM(): VM {
  const c = useCrowd();
  // rolling 60-minute trace of queue release vs darshan completion (the model's own seed series uses another scale)
  const trace = useRef<{ clock: number; pts: { t: string; a: number; b: number }[] }>({ clock: -1, pts: [] });
  if (trace.current.clock !== c.clock) {
    const t = trace.current;
    if (!t.pts.length) {
      for (let i = 59; i >= 1; i--) {
        const w = 1 + 0.12 * Math.sin((c.clock - i) / 5.5) + 0.05 * Math.sin((c.clock - i) / 1.7);
        t.pts.push({ t: hhmm(c.clock - i), a: Math.round(c.releaseRateVal * w), b: Math.round(c.darshanRate * (2 - w)) });
      }
    }
    t.pts.push({ t: hhmm(c.clock), a: Math.round(c.releaseRateVal), b: Math.round(c.darshanRate) });
    if (t.pts.length > 60) t.pts.splice(0, t.pts.length - 60);
    t.clock = c.clock;
  }
  return useMemo<VM>(() => {
    const cells = c.queueState.cells, xcells = c.queueState.xcells;
    const zones: QZone[] = QSTAGES.map(st => {
      const lines = QLINES.filter(l => (STAGE_ACTIVE_LINES[st.id] || []).includes(l.id));
      const count = lines.reduce((a, l) => a + (cells[qKey(l.id, st.id)] || 0), 0);
      const area = lines.reduce((a, l) => a + cellArea(l.id, st), 0);
      const through = lines.reduce((a, l) => a + l.rate, 0);
      return {
        id: st.sector, name: st.name, count, cap: Math.round(area * 3.5), density: area ? count / area : null,
        dwell: Math.max(1, Math.round(st.len / 28)), inRate: through, outRate: Math.round(through * 0.94), eta: through ? Math.round(count / through) : null,
      };
    });
    const holding: HoldArea[] = c.holdingComplexes.map(ha => {
      const count = ha.rooms.reduce((a, r) => a + r.currentCount, 0);
      const inflow = ha.rooms.reduce((a, r) => a + r.inflowRate, 0);
      const outflow = ha.rooms.reduce((a, r) => a + r.outflowRate, 0);
      const open = ha.rooms.filter(r => r.gateStatus !== 'closed');
      const fullest = [...open].sort((a, b) => b.currentCount / b.capacity - a.currentCount / a.capacity)[0];
      const emptiest = [...ha.rooms].sort((a, b) => a.currentCount / a.capacity - b.currentCount / b.capacity)[0];
      return {
        id: ha.id, name: ha.name.split('·')[0].trim(), location: ha.location, count, cap: ha.totalCapacity, inflow,
        wait: outflow ? Math.round(count / outflow) : null, nextRelease: fullest?.id ?? null, fillNext: emptiest?.id ?? null,
        boxes: ha.rooms.map(r => ({ id: r.id, count: r.currentCount, cap: r.capacity, gate: r.gateStatus })),
      };
    });
    const inQueue = zones.reduce((a, z) => a + (z.count || 0), 0);
    const holdTotal = holding.reduce((a, h) => a + (h.count || 0), 0);
    const exiting = XSTAGES.reduce((a, s) => a + (xcells[s.id] || 0), 0);
    const worst = [...zones].sort((a, b) => (b.density || 0) - (a.density || 0))[0];
    const next = [...holding].sort((a, b) => (b.count || 0) / b.cap - (a.count || 0) / a.cap)[0];
    const crit = c.alerts.some(a => a.lvl === 'crit'), warn = c.alerts.length > 0;
    const level: Level = crit ? 'HIGH' : warn ? 'ELEVATED' : 'NORMAL';
    const forecast = Math.max(0, Math.round(c.inside + (c.rateIn - c.rateOut) * 12));
    const rail = ORIGINS.filter(o => o.mode === 'Rail');
    const sum = (ids: string[], src: Record<string, number>) => ids.reduce((a, id) => a + (src[id] || 0), 0);
    const railIds = rail.map(o => o.id);
    const totalSrc = Object.values(c.srcTotals).reduce((a, b) => a + b, 0) || 1;
    const xs = (id: string) => { const s = XSTAGES.find(x => x.id === id)!; return { people: xcells[id] || 0, rate: Math.round((xcells[id] || 0) / Math.max(1, xArea(s) / 40)) }; };

    return {
      clock: hhmm(c.clock),
      kpis: { total: c.arrivedToday, approaches: c.pending.reduce((a, p) => a + p.n, 0), holding: holdTotal, inQueue, inside: c.inside, insideCap: CONFIG.CAPACITY, darshan: c.darshanTotalToday, exited: Math.max(0, c.darshanTotalToday - exiting) },
      level, levelNote: crit ? 'Crowd pressure' : warn ? 'Watch closely' : 'Flow under control',
      city: {
        nodes: [
          { id: 'RAIL', name: 'Railway Station', total: sum(railIds, c.srcTotals), rate: sum(railIds, c.srcRate), x: 300, y: 70, kind: 'rail', place: 'above' },
          { id: 'BUS', name: NODES['PNBS']?.name || 'PNBS Bus Station', total: c.srcTotals['PNBS'] || 0, rate: c.srcRate['PNBS'] || 0, x: 318, y: 190, kind: 'bus', place: 'below' },
          { id: 'LOCAL', name: 'Local / walk-in', total: c.srcTotals['LOCAL'] || 0, rate: c.srcRate['LOCAL'] || 0, x: 58, y: 108, kind: 'walk', place: 'above' },
          { id: 'TEMPLE', name: 'Kanaka Durga Temple', total: c.inside, rate: c.darshanRate, x: 128, y: 182, kind: 'temple', place: 'below' },
        ],
        entries: [
          { name: 'SCR trains', value: sum(railIds, c.srcTotals), pct: Math.round(sum(railIds, c.srcTotals) / totalSrc * 100) },
          { name: 'APSRTC buses', value: c.srcTotals['PNBS'] || 0, pct: Math.round((c.srcTotals['PNBS'] || 0) / totalSrc * 100) },
          { name: 'Local / walk-in', value: c.srcTotals['LOCAL'] || 0, pct: Math.round((c.srcTotals['LOCAL'] || 0) / totalSrc * 100) },
        ],
        total: totalSrc,
      },
      holding, zones,
      temple: { inside: c.inside, cap: CONFIG.CAPACITY, inRate: c.releaseRateVal, outRate: c.darshanRate },
      services: [
        { id: 'prasadam', name: 'Prasadam area', ...xs('XS6') },
        { id: 'annadanam', name: 'Annaprasadam', ...xs('XS5') },
        { id: 'exit', name: 'Exit corridor', ...xs('XS7') },
      ],
      chart: { points: [...trace.current.pts], title: 'Temple throughput — last 60 minutes', aName: 'Queue release', bName: 'Darshan completion' },
      decisions: [
        { icon: <Play className="w-3.5 h-3.5" />, label: 'Next release', value: next ? `${next.id} → Box ${next.nextRelease ?? '—'}` : '—', note: c.queueState.holdOpen ? 'Release on schedule' : 'Release gate is held', tone: 'info' },
        { icon: <ShieldAlert className="w-3.5 h-3.5" />, label: 'Bottleneck', value: worst ? `${worst.id} · ${pct(worst.count, worst.cap) ?? 0}%` : '—', note: 'Monitor & manage flow', right: worst?.eta != null ? `ETA ${worst.eta} min` : undefined, tone: 'bad' },
        { icon: <TrendingUp className="w-3.5 h-3.5" />, label: 'Forecast', value: `${fmt(forecast)} / ${fmt(CONFIG.CAPACITY)}`, note: 'Temple-zone occupancy', right: 'in 12 min', tone: 'warn' },
        { icon: <Video className="w-3.5 h-3.5" />, label: 'Active cameras', value: `${c.queueState.cams.length} / ${c.queueState.cams.length}`, note: 'Live monitoring', right: 'All online', tone: 'ok' },
        { icon: <Users className="w-3.5 h-3.5" />, label: 'Crowd update', value: c.congested ? 'Congestion' : 'Stable', note: c.congested ? `Choke on segment ${c.congested}` : 'Flow under control', tone: c.congested ? 'warn' : 'ok' },
        { icon: <ShieldAlert className="w-3.5 h-3.5" />, label: 'Alert level', value: level || '—', note: c.alerts[0]?.ttl ?? 'No active alerts', tone: crit ? 'bad' : warn ? 'warn' : 'ok' },
      ],
      cameras: { online: c.queueState.cams.length, total: c.queueState.cams.length },
      queueWait: zones.reduce((a, z) => a + (z.eta || 0), 0),
    };
  }, [c]);
}

/* ------------------------------------------------------------------ small pieces */
const TONE: Record<Decision['tone'], string> = {
  ok: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20', warn: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
  bad: 'text-rose-500 bg-rose-500/10 border-rose-500/20', info: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
  muted: 'text-neutral-400 bg-neutral-500/10 border-neutral-500/20',
};
const TONE_TEXT: Record<Decision['tone'], string> = { ok: 'text-emerald-500', warn: 'text-amber-500', bad: 'text-rose-500', info: 'text-blue-500', muted: 'text-neutral-400' };

const PanelHead: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => (
  <div className="px-3 py-2 border-b border-neutral-200 dark:border-[#262c39] flex items-center justify-between gap-2">
    <div className="min-w-0">
      <h2 className="text-[13px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 truncate">{title}</h2>
      {sub && <p className={`text-[10px] ${SUB} truncate`}>{sub}</p>}
    </div>
    {right}
  </div>
);

const Kpi: React.FC<{ label: string; value: React.ReactNode; accent?: string; missing?: boolean }> = ({ label, value, accent = 'text-neutral-900 dark:text-neutral-100', missing }) => (
  <div className="px-3 py-1.5 border-l first:border-l-0 border-neutral-200 dark:border-[#262c39] min-w-0">
    <div className={`text-[9px] uppercase tracking-wider font-semibold ${SUB} whitespace-nowrap`}>{label}</div>
    <div className={`text-base font-extrabold tracking-tight font-mono ${missing ? 'text-neutral-400' : accent}`}>{value}</div>
  </div>
);

const NODE_ICON: Record<CityNode['kind'], React.ReactNode> = {
  rail: <Train className="w-3 h-3" />, bus: <Bus className="w-3 h-3" />, walk: <Footprints className="w-3 h-3" />,
  junction: <MapPin className="w-3 h-3" />, temple: <Landmark className="w-3 h-3" />,
};

const PLACE: Record<NonNullable<CityNode['place']>, string> = {
  above: '-translate-x-1/2 -translate-y-full -mt-2', below: '-translate-x-1/2 mt-2',
  right: 'ml-2.5 -translate-y-1/2', left: '-translate-x-full -ml-2.5 -translate-y-1/2',
};

/** Stylised city schematic (not to scale): river, hill, the approaches and their live counts. */
const CityMap: React.FC<{ nodes: CityNode[] }> = ({ nodes: all }) => {
  // junction markers appear only where this server has a camera at that junction
  const nodes = all.filter(n => n.kind !== 'junction' || n.total != null);
  const temple = nodes.find(n => n.kind === 'temple');
  return (
    <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 dark:bg-[#0d1220] border border-neutral-200 dark:border-[#262c39]">
      <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full">
        <defs>
          <pattern id="ov-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M20 0H0V20" fill="none" className="stroke-slate-200 dark:stroke-[#161d30]" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#ov-grid)" />
        {/* Krishna river */}
        <path d="M-10 236 C 70 196, 150 214, 214 240 S 340 268, 410 232 L 410 310 L -10 310 Z" className="fill-sky-200 dark:fill-[#12305c]" />
        <text x="250" y="284" className="fill-sky-600 dark:fill-sky-300/70" fontSize="10" fontStyle="italic" transform="rotate(4 250 284)">Krishna River</text>
        {/* Indrakeeladri hill */}
        <path d="M66 204 C 74 158, 114 142, 146 158 C 172 170, 168 204, 136 212 C 106 220, 74 220, 66 204 Z" className="fill-emerald-100 dark:fill-emerald-900/30 stroke-emerald-300 dark:stroke-emerald-700/50" strokeWidth="1" />
        <text x="200" y="110" textAnchor="middle" className="fill-slate-400 dark:fill-slate-600" fontSize="15" fontWeight="700" letterSpacing="2">VIJAYAWADA</text>
        {/* approach flows toward the temple */}
        {temple && nodes.filter(n => n.kind !== 'temple').map(n => (
          <path key={n.id} d={`M${n.x} ${n.y} Q ${(n.x + temple.x) / 2} ${(n.y + temple.y) / 2 - 22}, ${temple.x} ${temple.y}`} fill="none"
            className={n.total == null ? 'stroke-slate-300 dark:stroke-slate-700' : 'stroke-blue-500/70'} strokeWidth={n.kind === 'junction' ? 1.2 : 2} strokeDasharray="5 5">
            {n.total != null && <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.2s" repeatCount="indefinite" />}
          </path>
        ))}
        {nodes.map(n => <circle key={n.id} cx={n.x} cy={n.y} r={n.kind === 'temple' ? 6 : 4} className={n.kind === 'temple' ? 'fill-rose-500' : n.total == null ? 'fill-slate-400' : 'fill-blue-500'} />)}
        {temple && <circle cx={temple.x} cy={temple.y} r="11" fill="none" className="stroke-rose-500/50" strokeWidth="1.5"><animate attributeName="r" values="8;14;8" dur="2.4s" repeatCount="indefinite" /></circle>}
      </svg>
      {nodes.map(n => (
        <div key={n.id} className={`absolute pointer-events-none ${PLACE[n.place || 'below']}`} style={{ left: `${n.x / 4}%`, top: `${n.y / 3}%` }}>
          <div className={`px-1.5 py-1 rounded-md border text-[9.5px] leading-tight shadow-sm whitespace-nowrap bg-white/95 dark:bg-[#141a2a]/95 ${n.kind === 'temple' ? 'border-rose-400/60' : 'border-neutral-200 dark:border-[#2b3550]'}`}>
            <div className="flex items-center gap-1 font-semibold text-neutral-700 dark:text-neutral-200">
              <span className={n.kind === 'temple' ? 'text-rose-500' : 'text-blue-500'}>{NODE_ICON[n.kind]}</span>{n.name}
            </div>
            <div className="font-mono">
              <span className="font-bold text-neutral-900 dark:text-neutral-100">{fmt(n.total)}</span>
              {n.rate != null && <span className="text-emerald-500"> · {fmt(n.rate)}/min</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ page */
const OverviewInner: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>(() => {
    try { return (localStorage.getItem('overview_mode') as Mode) === 'model' ? 'model' : 'live'; } catch { return 'live'; }
  });
  useEffect(() => { try { localStorage.setItem('overview_mode', mode); } catch { /* private mode */ } }, [mode]);

  const live = useLiveVM(mode === 'live');
  const model = useModelVM();
  const vm = mode === 'live' ? live : model;
  const isLive = mode === 'live';
  const k = vm.kpis;
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });
  const levelCls = vm.level === 'HIGH' ? 'bg-rose-500/10 border-rose-500/30 text-rose-500' : vm.level === 'ELEVATED' ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
    : vm.level === 'NORMAL' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' : 'bg-neutral-500/10 border-neutral-500/20 text-neutral-400';
  const zonesTotal = vm.zones.some(z => z.count != null) ? vm.zones.reduce((a, z) => a + (z.count || 0), 0) : null;

  const ChartTip = ({ active, payload, label }: any) => !active || !payload?.length ? null : (
    <div className="bg-white dark:bg-[#181b24] border border-neutral-200 dark:border-[#2b3140] rounded-lg p-2 shadow-xl text-[11px]">
      <div className="font-bold mb-1 text-neutral-900 dark:text-neutral-100">{label}</div>
      {payload.map((e: any) => <div key={e.name} className="flex justify-between gap-3" style={{ color: e.color }}><span>{e.name}</span><b>{fmt(e.value)}</b></div>)}
    </div>
  );

  return (
    <div className="space-y-2.5 text-neutral-900 dark:text-neutral-100">
      {/* ============ 1. COMMAND HEADER + KPI STRIP ============ */}
      <div className={CARD}>
        <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-blue-500 text-white flex items-center justify-center shadow-sm shadow-blue-500/20"><Landmark className="w-4.5 h-4.5" /></div>
            <div>
              <h1 className="text-sm font-bold tracking-tight leading-tight">Vijayawada Police</h1>
              <p className={`text-[11px] ${SUB} leading-tight`}>Durga Navaratri Crowd Command · Overview</p>
            </div>
          </div>
          <span className="text-[11px] font-mono text-neutral-600 dark:text-neutral-300">{today} · {vm.clock} IST</span>
          {isLive
            ? <span className="text-[10px] px-2 py-0.5 rounded-md font-semibold border flex items-center gap-1 text-emerald-500 bg-emerald-500/10 border-emerald-500/20"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />LIVE · cameras</span>
            : <span className="text-[10px] px-2 py-0.5 rounded-md font-semibold border flex items-center gap-1 text-violet-500 bg-violet-500/10 border-violet-500/20"><Radio className="w-2.5 h-2.5" />SIMULATION · not camera data</span>}
          <div className={`hidden lg:flex items-center gap-1 text-[10px] ${SUB} mx-auto`}>
            {['City', 'Holding', 'Queue', 'Temple', 'Exit'].map((st, i) => (
              <React.Fragment key={st}>{i > 0 && <ChevronRight className="w-3 h-3" />}<span className="px-1.5 py-0.5 rounded border border-neutral-200 dark:border-[#262c39]">{st}</span></React.Fragment>
            ))}
          </div>
          <div className="flex rounded-md overflow-hidden border border-neutral-200 dark:border-[#262c39] text-[10px] font-semibold ml-auto">
            {([['live', 'Live cameras'], ['model', 'Simulation model']] as [Mode, string][]).map(([m, l]) => (
              <button key={m} onClick={() => setMode(m)} className={`px-2.5 py-1 cursor-pointer ${mode === m ? 'bg-blue-500 text-white' : 'bg-neutral-50 dark:bg-[#14171f] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-[#1b1f2a]'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 border-t border-neutral-200 dark:border-[#262c39]">
          <Kpi label="Total devotees" value={fmt(k.total)} missing={k.total == null} />
          <Kpi label={isLive ? 'Arrivals · 15 min' : 'City approaches'} value={fmt(k.approaches)} missing={k.approaches == null} accent="text-blue-500" />
          <Kpi label="Holding" value={fmt(k.holding)} missing={k.holding == null} accent="text-violet-500" />
          <Kpi label="In queue" value={fmt(k.inQueue)} missing={k.inQueue == null} accent="text-amber-500" />
          <Kpi label="Inside temple" value={<>{fmt(k.inside)}{k.insideCap != null && <span className={`text-[10px] font-semibold ${SUB}`}> / {fmt(k.insideCap)}</span>}</>} missing={k.inside == null} />
          <Kpi label="Darshan completed" value={fmt(k.darshan)} missing={k.darshan == null} accent="text-emerald-500" />
          <Kpi label={isLive ? 'Returned' : 'Exited'} value={fmt(k.exited)} missing={k.exited == null} />
          <div className={`px-3 py-1.5 border-l flex items-center gap-2 xl:rounded-br-xl ${levelCls}`}>
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-wider font-semibold opacity-80">Alert level</div>
              <div className="text-sm font-extrabold leading-tight">{vm.level ?? '—'}</div>
              <div className="text-[9px] opacity-80 truncate">{vm.levelNote}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ 2. CITY · DIGITAL TWIN CORE · DECISIONS ============ */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-2.5">
        {/* City */}
        <div className={`${CARD} xl:col-span-3 flex flex-col`}>
          <PanelHead title="City — Live arrivals & movement" sub={isLive ? 'People counted leaving each hub toward the temple, since 00:00' : 'Modelled arrivals by source'} right={<span className={`text-[10px] font-mono ${SUB}`}>{vm.clock} IST</span>} />
          <div className="p-2.5 flex-1 flex flex-col gap-2">
            <CityMap nodes={vm.city.nodes} />
            <div className="text-[11px]">
              <div className={`text-[10px] font-semibold uppercase tracking-wider ${SUB} mb-1`}>City entry (today)</div>
              {vm.city.entries.map(e => (
                <div key={e.name} className="flex items-center justify-between py-0.5 border-b border-neutral-100 dark:border-[#1d222d] last:border-0">
                  <span className="truncate text-neutral-600 dark:text-neutral-300">{e.name}</span>
                  <span className="font-mono font-semibold flex items-center gap-2">{e.value == null ? <NoCam /> : <>{fmt(e.value)}<span className={`text-[10px] w-9 text-right ${SUB}`}>{e.pct ?? 0}%</span></>}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1 mt-0.5 border-t border-neutral-200 dark:border-[#262c39] font-bold">
                <span>Total</span><span className="font-mono">{fmt(vm.city.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Digital twin core */}
        <div className={`${CARD} xl:col-span-6 flex flex-col`}>
          <PanelHead title="Digital Twin Core" sub="Live flow from city to darshan" right={
            <div className="flex items-center gap-2 text-right">
              <div><div className="text-[11px] font-bold leading-tight">Kanaka Durga Temple</div><div className={`text-[9px] ${SUB} leading-tight`}>Sri Durga Malleswara Swamy Varla Devasthanam</div></div>
              <Landmark className="w-6 h-6 text-amber-500" />
            </div>} />
          <div className="p-2.5 grid grid-cols-1 lg:grid-cols-[minmax(150px,1fr)_auto_minmax(0,2.6fr)_auto_minmax(120px,0.9fr)_auto_minmax(150px,1fr)] gap-2 items-stretch">
            {/* holding */}
            <div className="flex flex-col gap-1.5">
              <div className={`text-[10px] font-semibold ${SUB}`}>Holding areas</div>
              {vm.holding.map(h => (
                <div key={h.id} className="flex-1 rounded-lg border border-neutral-200 dark:border-[#262c39] bg-neutral-50 dark:bg-[#14171f] px-2 py-1.5 border-l-2 border-l-violet-500">
                  <div className="flex items-center justify-between gap-1">
                    <span className={`text-[9px] font-mono ${SUB}`}>{h.id}</span>
                    {h.count == null ? <NoCam /> : <span className={`text-[9px] ${SUB} truncate`}>Next release · {h.nextRelease ?? '—'}</span>}
                  </div>
                  <div className={`text-lg font-extrabold font-mono leading-tight ${h.count == null ? 'text-neutral-400' : 'text-emerald-500'}`}>{fmt(h.count)}</div>
                  <div className={`text-[9px] font-mono ${SUB}`}>{h.inflow == null ? `capacity ${fmt(h.cap)}` : `${fmt(h.inflow)}/min · cap ${fmt(h.cap)}`}</div>
                </div>
              ))}
            </div>
            <div className="hidden lg:flex items-center text-emerald-500"><ArrowRight className="w-4 h-4" /></div>
            {/* queue zones */}
            <div className="rounded-lg border border-neutral-200 dark:border-[#262c39] p-2 flex flex-col">
              <div className="text-center mb-1.5">
                <div className={`text-[10px] font-semibold ${SUB}`}>Queue zones ({vm.zones[0]?.id ?? 'S'} to {vm.zones[vm.zones.length - 1]?.id ?? ''})</div>
                <div className="text-xs font-bold font-mono text-emerald-500">{fmt(zonesTotal)} <span className={`text-[9px] font-normal ${SUB}`}>in queue</span></div>
              </div>
              <div className="flex-1 grid grid-cols-3 2xl:grid-cols-6 gap-1.5">
                {vm.zones.map(z => {
                  const p = pct(z.count, z.cap); const b = band(p);
                  return (
                    <div key={z.id} className={`rounded-md border px-1 py-2 flex flex-col items-center justify-center text-center bg-neutral-50 dark:bg-[#14171f] ${p != null && p > 75 ? 'border-rose-400/60 ring-1 ring-rose-400/30' : 'border-neutral-200 dark:border-[#262c39]'}`}>
                      <div className={`text-[9px] font-mono ${SUB}`}>{zoneLabelFromId(z.id)}</div>
                      <div className="text-[11px] font-bold font-mono leading-tight">{fmt(z.count)}<span className={`text-[8.5px] font-normal ${SUB}`}>/{fmt(z.cap)}</span></div>
                      {z.count == null && z.passed != null
                        ? <div className="text-[8.5px] font-mono text-emerald-500 leading-tight">{fmt(z.passed)}<br />passed</div>
                        : <div className={`text-[9px] font-mono ${b.text}`}>{z.eta == null ? '—' : `${z.eta} min`}</div>}
                    </div>
                  );
                })}
              </div>
              <div className={`text-center text-[9px] mt-1.5 ${SUB} flex items-center justify-center gap-1`}>Flow towards temple <ArrowRight className="w-3 h-3" /></div>
            </div>
            <div className="hidden lg:flex items-center text-emerald-500"><ArrowRight className="w-4 h-4" /></div>
            {/* temple */}
            <div className="rounded-lg border border-amber-400/40 bg-amber-500/5 p-2 flex flex-col items-center justify-between text-center">
              <div>
                <div className={`text-[10px] font-semibold ${SUB}`}>Temple</div>
                <div className="text-sm font-extrabold font-mono text-emerald-500">{fmt(vm.temple.inside)}<span className={`text-[10px] font-normal ${SUB}`}> / {fmt(vm.temple.cap)}</span></div>
              </div>
              <Landmark className="w-9 h-9 text-amber-500 my-1" />
              {vm.temple.inside == null ? <NoCam /> : (
                <div className={`w-full flex justify-between text-[9px] font-mono ${SUB}`}>
                  <span><b className="text-emerald-500">{fmt(vm.temple.inRate)}</b>/min in</span><span><b className="text-violet-500">{fmt(vm.temple.outRate)}</b>/min out</span>
                </div>
              )}
            </div>
            <div className="hidden lg:flex items-center text-emerald-500"><ArrowRight className="w-4 h-4" /></div>
            {/* services */}
            <div className="flex flex-col gap-1.5">
              <div className={`text-[10px] font-semibold ${SUB}`}>Post-temple services</div>
              {vm.services.map(s => (
                <div key={s.id} className="flex-1 rounded-lg border border-neutral-200 dark:border-[#262c39] bg-neutral-50 dark:bg-[#14171f] px-2 py-1.5 flex items-center gap-2">
                  <span className="text-violet-500 shrink-0">{s.id === 'prasadam' ? <Gift className="w-4 h-4" /> : s.id === 'annadanam' ? <Utensils className="w-4 h-4" /> : <DoorOpen className="w-4 h-4" />}</span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold truncate">{s.name}</div>
                    {s.people == null ? <NoCam /> : <div className={`text-[9px] font-mono ${SUB}`}><b className="text-neutral-800 dark:text-neutral-100">{fmt(s.people)}</b> people · <span className="text-emerald-500">{fmt(s.rate)}/min</span></div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* throughput chart */}
          <div className="px-2.5 pb-2.5 flex-1 flex flex-col min-h-[190px]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold">{vm.chart.title}</span>
              <span className={`flex items-center gap-3 text-[9.5px] ${SUB}`}>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{vm.chart.aName}</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />{vm.chart.bName}</span>
              </span>
            </div>
            <div className="flex-1 min-h-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={vm.chart.points} margin={{ top: 4, right: 6, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ov-a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.28} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-neutral-200 dark:text-[#232836]" vertical={false} />
                  <XAxis dataKey="t" tick={{ fontSize: 9, fill: '#8b93a7' }} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis tick={{ fontSize: 9, fill: '#8b93a7' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : `${v}`)} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="a" name={vm.chart.aName} stroke="#10b981" strokeWidth={1.8} fill="url(#ov-a)" dot={false} isAnimationActive={false} />
                  <Area type="monotone" dataKey="b" name={vm.chart.bName} stroke="#8b5cf6" strokeWidth={1.6} fill="none" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Operational decisions */}
        <div className={`${CARD} xl:col-span-3 flex flex-col`}>
          <PanelHead title="Operational decisions" sub={isLive ? 'From camera counts only' : 'From the crowd model'} />
          <div className="p-2.5 flex-1 flex flex-col gap-1.5">
            {vm.decisions.map(d => (
              <div key={d.label} className="flex-1 rounded-lg border border-neutral-200 dark:border-[#262c39] bg-neutral-50 dark:bg-[#14171f] px-2.5 py-1.5 flex items-center gap-2.5">
                <span className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${TONE[d.tone]}`}>{d.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className={`text-[9.5px] ${SUB}`}>{d.label}</div>
                  <div className={`text-[12px] font-bold font-mono truncate ${TONE_TEXT[d.tone]}`}>{d.value}</div>
                  <div className={`text-[9.5px] ${SUB} truncate`}>{d.note}</div>
                </div>
                {d.right && <span className={`text-[10px] font-mono font-semibold whitespace-nowrap ${TONE_TEXT[d.tone]}`}>{d.right}</span>}
              </div>
            ))}
            <button onClick={() => navigate('/alerts')} className="mt-0.5 rounded-lg border border-neutral-200 dark:border-[#262c39] py-1.5 text-[11px] font-semibold text-neutral-600 dark:text-neutral-300 hover:border-blue-500/50 hover:text-blue-500 flex items-center justify-center gap-1.5 cursor-pointer">
              View all alerts <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* ============ 3. QUEUE ZONES ============ */}
      <div className={CARD}>
        <PanelHead title="Queue zones — Live status" right={
          <span className={`text-[10.5px] ${SUB}`}>Total in queue: <b className="text-neutral-900 dark:text-neutral-100 font-mono">{fmt(zonesTotal)}</b>
            <span className="mx-2">·</span>Average wait to darshan: <b className="text-amber-500 font-mono">{vm.queueWait == null ? '—' : `${vm.queueWait} min`}</b></span>} />
        <div className="p-2.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-2">
          {vm.zones.map(z => {
            const p = pct(z.count, z.cap); const b = band(p);
            return (
              <div key={z.id} className={`rounded-lg border px-2.5 py-2 bg-neutral-50 dark:bg-[#14171f] ${p != null && p > 75 ? 'border-rose-400/60' : 'border-neutral-200 dark:border-[#262c39]'}`}>
                <div className="flex items-center justify-between gap-1.5">
                  <span className="text-[12px] font-bold font-mono">{zoneLabelFromId(z.id)}</span>
                  <span className={`text-[9.5px] ${SUB} truncate flex-1`}>{z.name}</span>
                  {z.count != null ? <span className={`w-1.5 h-1.5 rounded-full ${b.bar}`} /> : z.passed != null ? <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold border text-emerald-500 bg-emerald-500/10 border-emerald-500/20 whitespace-nowrap">Pass count only</span> : <NoCam />}
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <span className="flex items-center gap-1 font-mono"><Users className={`w-3.5 h-3.5 ${SUB}`} /><b className={`text-sm ${z.count == null ? 'text-neutral-400' : b.text}`}>{fmt(z.count)}</b><span className={`text-[10px] ${SUB}`}>/ {fmt(z.cap)}</span></span>
                  <span className={`text-[10px] font-mono ${SUB}`}>{p == null ? '' : `${p}%`}</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-neutral-200 dark:bg-[#232836] overflow-hidden"><div className={`h-full rounded-full ${b.bar}`} style={{ width: `${Math.min(100, p ?? 0)}%` }} /></div>
                {z.count == null && z.passed != null && <div className="mt-1 text-[9.5px] font-mono text-emerald-500"><b>{fmt(z.passed)}</b> passed since 00:00</div>}
                <div className={`mt-1.5 grid grid-cols-4 gap-1 text-[8.5px] ${SUB}`}>
                  {[['Density', z.density == null ? '—' : `${z.density.toFixed(1)} p/m²`], ['Dwell', z.dwell == null ? '—' : `${z.dwell} min`],
                    ['In/Out', z.inRate == null ? '—' : `${fmt(z.inRate)}/${z.outRate == null ? '—' : fmt(z.outRate)}`], ['ETA', z.eta == null ? '—' : `${z.eta} min`]].map(([l, v]) => (
                    <div key={l}><div>{l}</div><div className="font-mono font-semibold text-neutral-700 dark:text-neutral-200 truncate">{v}</div></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4. HOLDING AREAS BY BOXES ============ */}
      <div className={CARD}>
        <PanelHead title="Holding areas — Live occupancy (by boxes)" right={
          <div className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9.5px] ${SUB}`}>
            {[['bg-emerald-500', 'Available (<50%)'], ['bg-violet-500', 'Filling (50–75%)'], ['bg-amber-500', 'High (76–90%)'], ['bg-rose-500', 'Full (>90%)']].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-sm ${c}`} />{l}</span>
            ))}
          </div>} />
        <div className={`p-2.5 grid grid-cols-1 ${vm.holding.length > 2 ? 'xl:grid-cols-3' : 'xl:grid-cols-2'} gap-2.5`}>
          {vm.holding.map(h => (
            <div key={h.id} className="rounded-lg border border-neutral-200 dark:border-[#262c39] p-2">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-bold flex items-center gap-1.5"><Warehouse className="w-3.5 h-3.5 text-violet-500" />{h.name} <span className="text-violet-500 font-mono">({h.id})</span>
                    {/* live video wall of all box cameras of this area, composed by the backend (backend/app/videowall.py) */}
                    {h.id === 'HA-1' && (
                      <a href={`/api/holding/${h.id}/wall`} title="Live video wall of all box cameras"
                        className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-violet-400/60 text-violet-500 hover:bg-violet-500 hover:text-white text-[10.5px] font-semibold transition-colors">
                        ▶ Video wall
                      </a>
                    )}
                  </div>
                  <div className={`text-[9.5px] ${SUB} truncate flex items-center gap-1`}><MapPin className="w-2.5 h-2.5 shrink-0" />{h.location}</div>
                </div>
                <div className="flex items-center gap-3 text-[9.5px]">
                  <div><div className="font-mono"><b className={`text-sm ${h.count == null ? 'text-neutral-400' : 'text-amber-500'}`}>{fmt(h.count)}</b><span className={SUB}> / {fmt(h.cap)}</span></div><div className={SUB}>{h.boxes.length} boxes</div></div>
                  {h.count == null ? <NoCam text="No camera yet" /> : <>
                    <div><div className={SUB}>Inflow</div><div className="font-mono font-semibold text-emerald-500">{fmt(h.inflow)}/min</div></div>
                    <div><div className={SUB}>Est. wait</div><div className="font-mono font-semibold text-amber-500">{h.wait == null ? '—' : `${h.wait} min`}</div></div>
                    <div className="px-1.5 py-0.5 rounded border border-violet-400/50"><div className={SUB}>Next release</div><div className="font-mono font-semibold text-violet-500">{h.nextRelease ?? '—'}</div></div>
                    <div className="px-1.5 py-0.5 rounded border border-sky-400/50"><div className={SUB}>Fill next</div><div className="font-mono font-semibold text-sky-500">{h.fillNext ?? '—'}</div></div>
                  </>}
                </div>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-7 gap-1.5">
                {h.boxes.map(bx => {
                  const p = pct(bx.count, bx.cap); const b = band(p);
                  const tag = bx.id === h.nextRelease ? 'Next release' : bx.id === h.fillNext ? 'Fill next' : null;
                  return (
                    <div key={bx.id} title={`${bx.id} · gate ${bx.gate}`} className={`relative rounded-md border px-1.5 pt-1 pb-1.5 bg-neutral-50 dark:bg-[#14171f] ${tag === 'Next release' ? 'border-violet-400 ring-1 ring-violet-400/40' : tag ? 'border-sky-400 ring-1 ring-sky-400/40' : 'border-neutral-200 dark:border-[#262c39]'}`}>
                      <div className={`text-[8.5px] font-mono ${SUB} truncate`}>{bx.id.replace(/^HA-?\d-?/i, '') || bx.id}</div>
                      <div className="text-[11px] font-bold font-mono leading-tight">{fmt(bx.count)}<span className={`text-[8.5px] font-normal ${SUB}`}>/{bx.cap}</span></div>
                      <div className="mt-1 h-[3px] rounded-full bg-neutral-200 dark:bg-[#232836] overflow-hidden"><div className={`h-full ${b.bar}`} style={{ width: `${Math.min(100, p ?? 0)}%` }} /></div>
                      {tag && <span className={`absolute -top-1.5 right-1 px-1 rounded text-[7.5px] font-bold text-white ${tag === 'Next release' ? 'bg-violet-500' : 'bg-sky-500'}`}>{tag}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const OverviewPage: React.FC = () => (
  <CrowdProvider>
    <OverviewInner />
  </CrowdProvider>
);
